import { useMemo } from "react";
import { MimshakService } from "../../services/mimshakService";

import { Mimshak, PolicyOrAccount, Product, ProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { MimshakDataUtil } from "../../services/mimshakDataUtil";
import { fundService } from "../../services/fundService";
import { MimshakInsurenceUtil } from "../../services/MimshakInsurenceUtil";

// ✅ הגדרת Type עבור הערך המוחזר
export interface FundDataResult {
  flatMimshaks: Mimshak[];
  capitalFlatMimshaks: Mimshak[];
  pensionFlatMimshaks: Mimshak[];
  allPolicies: PolicyOrAccount[];
  allProducts: Product[];
  pensionProducts: Product[];
  liquidProducts: Product[];
  insuranceProducts: Product[];
  insuranceMimshakData: MimshakData[]; // מוצרי ביטוח
  pensionMimshakData: MimshakData[]; // MimshakData מסוננת למוצרי פנסיה בלבד
  educationMimshakData: MimshakData[]; // קרנות השתלמות בלבד
  capitalMimshakData: MimshakData[]; // כל הנתונים (הון נזיל)
  fundData: FundDataBasic[]; // אם יש מבנה מוגדר ל-fundData אפשר לעדכן כאן

}

export const useFundData = (filteredMimshaks: MimshakData[]): FundDataResult => {
  // ✅ מיון הנתונים פעם אחת בלבד
  const sortedMimshaks = useMemo(
    () => MimshakDataUtil.sortByHandlerAndProductType(filteredMimshaks || []),
    [filteredMimshaks]
  );

  // ✅ חילוץ Mimshak מהנתונים הממוינים
  const flatMimshaks = useMemo(
    () => sortedMimshaks
      .map((item) => item.data)
      .filter((item): item is Mimshak => !!item),
    [sortedMimshaks]
  );

  // ✅ שליפת פוליסות ומוצרים
  const allPolicies = useMemo(
    () => MimshakService.getAllPolicies(flatMimshaks || []),
    [flatMimshaks]
  );

  const allProducts = useMemo(
    () => MimshakService.getAllProducts(flatMimshaks || []),
    [flatMimshaks]
  );

  // ✅ סינון נתוני ביטוח ופנסיה (מהנתונים הממוינים)
  const insuranceMimshakData = useMemo(() => {
    const insuranceData = MimshakDataUtil.getInsuranceMimshakData(sortedMimshaks);
    // נרמול הכיסויים הביטוחיים
    insuranceData.forEach(mimshakData => {
      if (mimshakData.data) {
        mimshakData.data.products?.forEach(product => {
          MimshakInsurenceUtil.normalizeProductCoverages(product);
        });
      }
    });
    return insuranceData;
  }, [sortedMimshaks]);

  const pensionMimshakData = useMemo(() => {
    return MimshakDataUtil.getPensionMimshakData(sortedMimshaks);
  }, [sortedMimshaks]);

  // ✅ חילוץ flatMimshaks עבור פנסיה וביטוח (ללא מיון נוסף!)
  const pensionFlatMimshaks = useMemo(
    () => pensionMimshakData
      .map((item) => item.data)
      .filter((item): item is Mimshak => !!item),
    [pensionMimshakData]
  );

  const insuranceFlatMimshaks = useMemo(
    () => insuranceMimshakData
      .map((item) => item.data)
      .filter((item): item is Mimshak => !!item),
    [insuranceMimshakData]
  );

  // ✅ חלוקת נתוני Mimshak לפי סוג (מהנתונים הממוינים)
  const educationMimshakData = useMemo(() => {
    // Education = רק קרנות השתלמות
    return sortedMimshaks.filter(m => m.product_type === ProductType.EDUCATION_FUND);
  }, [sortedMimshaks]);

  const capitalMimshakData = useMemo(() => {
    // Capital = כל הנתונים הנזילים (הון נזיל - חיסכון, קרנות וכו')
    return MimshakDataUtil.getLiquidMimshakData(sortedMimshaks);
  }, [sortedMimshaks]);

  // ✅ חילוץ flatMimshaks עבור הון נזיל (ללא מיון נוסף!)
  const capitalFlatMimshaks = useMemo(
    () => capitalMimshakData
      .map((item) => item.data)
      .filter((item): item is Mimshak => !!item),
    [capitalMimshakData]
  );

  // ✅ סינון מוצרים מה-flatMimshaks
  const pensionProducts = useMemo(
    () => MimshakService.getAllProducts(pensionFlatMimshaks || []),
    [pensionFlatMimshaks]
  );

  const insuranceProducts = useMemo(
    () => MimshakService.getAllProducts(insuranceFlatMimshaks || []),
    [insuranceFlatMimshaks]
  );

  const liquidProducts = useMemo(
    () => MimshakService.getAllProducts(capitalFlatMimshaks || []),
    [capitalFlatMimshaks]
  );

  // ✅ שליפת קודים לצורך שליפה נוספת
  const pathCodes = useMemo(
    () => MimshakService.extractPathCodes(allPolicies),
    [allPolicies]
  );

  // ✅ שליפה לפי PathCodes → יקרה רק אם יש PathCodes

  const fundData = fundService.getFundsFromCacheByIds(pathCodes);

  return {
    flatMimshaks,
    capitalFlatMimshaks,
    pensionFlatMimshaks,
    allPolicies,
    allProducts,
    pensionProducts,
    liquidProducts,
    insuranceProducts,
    fundData,
    insuranceMimshakData,
    pensionMimshakData,
    educationMimshakData,
    capitalMimshakData,
  };
};
